import { Effect, Schema as S } from "effect";

/** Server task REST shapes (`rhysomn-server-v2` taskRoutes, Cairn parity). Decoded leniently. */

export const TaskStatus = S.Literals([
	"backlog",
	"todo",
	"in_progress",
	"done",
	"canceled",
]);
export type TaskStatus = typeof TaskStatus.Type;

export const TaskPriority = S.Literals([
	"urgent",
	"high",
	"medium",
	"low",
	"none",
]);
export type TaskPriority = typeof TaskPriority.Type;

/** `GET /tasks` sort keys the list offers (server `order_by`; default directions are server-side). */
export const TaskSort = S.Literals([
	"attention",
	"updated_at",
	"priority",
	"grade",
	"due",
]);
export type TaskSort = typeof TaskSort.Type;

/** `GET /tasks?graded=`: every task, only graded, or only never graded. */
export const GradeFilter = S.Literals(["all", "graded", "ungraded"]);
export type GradeFilter = typeof GradeFilter.Type;

export const Canonical = S.Struct({
	text: S.String,
	promoted_from_comment_id: S.optionalKey(S.NullOr(S.String)),
	promoted_at: S.optionalKey(S.NullOr(S.String)),
});
export type Canonical = typeof Canonical.Type;

const canonicals = S.optionalKey(S.Array(Canonical));

export const Task = S.Struct({
	id: S.String,
	parent_task_id: S.NullOr(S.String),
	depth: S.Number,
	title: S.String,
	description: S.NullOr(S.String),
	status: TaskStatus,
	priority: TaskPriority,
	sections: S.Struct({
		hypothesis: canonicals,
		evidence: canonicals,
		outcome: canonicals,
	}),
	attributes: S.NullOr(S.Record(S.String, S.Unknown)),
	assignee_id: S.NullOr(S.String),
	tags: S.Array(S.String),
	due: S.NullOr(S.String),
	/** Record ids; never rendered raw. */
	people: S.Array(S.String),
	created_by: S.String,
	created_at: S.String,
	updated_at: S.String,
	/** Latest scorecard overall grade; only `GET /tasks` sends it (absent elsewhere = ungraded). */
	grade: S.NullOr(S.Number).pipe(
		S.withDecodingDefaultKey(Effect.succeed(null)),
	),
});
export type Task = typeof Task.Type;

/** Circle-wide header counts, independent of the page's filters and paging. */
export const TaskTotals = S.Struct({
	open: S.Number,
	/** Done tasks updated in the last 7 days; an older server omits it. */
	doneSince: S.NullOr(S.Number).pipe(
		S.withDecodingDefaultKey(Effect.succeed(null)),
	),
	byStatus: S.Record(S.String, S.Number),
	graded: S.Number,
	avgGrade: S.NullOr(S.Number),
});
export type TaskTotals = typeof TaskTotals.Type;

export const TaskPage = S.Struct({
	tasks: S.Array(Task),
	cursor: S.NullOr(S.String),
	/** Absent from an older server: the header then counts the loaded rows. */
	totals: S.NullOr(TaskTotals).pipe(
		S.withDecodingDefaultKey(Effect.succeed(null)),
	),
});
export type TaskPage = typeof TaskPage.Type;

export const TaskEnvelope = S.Struct({ task: Task });

/** `GET /tasks/:id`: the task plus its direct children. */
export const TaskDetail = S.Struct({ task: Task, children: S.Array(Task) });

export const CommentType = S.Literals([
	"hypothesis",
	"evidence",
	"outcome",
	"discussion",
]);
export type CommentType = typeof CommentType.Type;

/** A task comment; `author_id` is an actor id, never rendered raw. */
export const TaskComment = S.Struct({
	id: S.String,
	task_id: S.String,
	author_id: S.String,
	body: S.String,
	type: CommentType,
	parent_comment_id: S.NullOr(S.String),
	created_at: S.String,
});
export type TaskComment = typeof TaskComment.Type;

export const TaskCommentList = S.Struct({ comments: S.Array(TaskComment) });

/** `GET /tasks/:id/tree`: the task and every descendant, shallowest first. */
export const TaskTree = S.Struct({ tasks: S.Array(Task) });

export const ScorecardResult = S.Struct({
	id: S.String,
	task_id: S.String,
	config_id: S.String,
	scores: S.Record(
		S.String,
		S.Struct({ score: S.Number, reasoning: S.String }),
	),
	overall_grade: S.Number,
	summary: S.String,
	reviewed_at: S.String,
	trigger_source: S.String,
});
export type ScorecardResult = typeof ScorecardResult.Type;

/** `GET /tasks/:id/scorecard`: every run, newest first. */
export const ScorecardHistory = S.Struct({ results: S.Array(ScorecardResult) });

export const ScorecardConfig = S.Struct({
	id: S.String,
	name: S.String,
	criteria: S.Array(
		S.Struct({
			key: S.String,
			label: S.String,
			weight: S.Number,
			description: S.String,
		}),
	),
	is_default: S.Boolean,
	created_at: S.String,
});
export type ScorecardConfig = typeof ScorecardConfig.Type;

export const ScorecardConfigList = S.Struct({
	configs: S.Array(ScorecardConfig),
});
